import React from "react";
import { Link } from "react-router-dom";
import LegalFooterBannerAndLinks from "components/legal/LegalFooterBannerAndLinks";
import LegalPagesBrandHeader from "components/legal/LegalPagesBrandHeader";
import {
  SLOT_PLANS,
  WELCOME_PROMO_CODE,
  personalBrokerSavingsUsd,
  welcomeSavingsUsd,
} from "constants/clientSlotPlans";

export default function Pricing() {
  return (
    <main className="min-h-screen bg-[#0B1437] px-5 py-6 text-white sm:py-8">
      <div className="mx-auto w-full max-w-6xl">
        <LegalPagesBrandHeader />
        <header className="text-center">
          <h1 className="text-3xl font-bold sm:text-4xl">Pricing</h1>
          <p className="mx-auto mt-3 max-w-3xl text-sm leading-relaxed text-gray-300 sm:text-base">
            One slot per tier. Welcome prices apply when you use the promo code at
            checkout.
          </p>
        </header>

        <div className="mt-10 space-y-10">
          <section>
            <h2 className="text-center text-sm font-bold uppercase tracking-wide text-white">
              Regular price
            </h2>
            <div className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {SLOT_PLANS.map((plan) => (
                <div
                  key={`reg-${plan.id}`}
                  className={`relative flex flex-col rounded-2xl border-2 border-white/10 bg-white/[0.04] p-5 shadow-sm ${plan.accent}`}
                >
                  {plan.badge ? (
                    <span className="absolute -top-2.5 right-3 rounded-full bg-brand-500 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white dark:bg-brand-400">
                      {plan.badge}
                    </span>
                  ) : null}
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                    {plan.period}
                  </p>
                  <h3 className="mt-1 text-lg font-bold text-white">{plan.name}</h3>
                  <p className="mt-1 text-xs text-gray-400">{plan.cadence}</p>
                  <p className="mt-4 text-sm text-gray-300">{plan.slots}</p>
                  <div className="mt-4 flex items-baseline gap-0.5">
                    <span className="text-3xl font-bold tabular-nums text-white">
                      {plan.regularPriceLabel}
                    </span>
                    <span className="text-sm font-medium text-gray-400">
                      {plan.priceSuffix}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h2 className="text-center text-sm font-bold uppercase tracking-wide text-emerald-400">
              Welcome offer with our broker partner
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-center text-base font-semibold text-white sm:text-lg">
              Save 30% on all our subscriptions
            </p>
            <p className="mx-auto mt-2 max-w-2xl text-center text-sm text-gray-300">
              Use PromoCode:{" "}
              <code className="rounded-md bg-white/10 px-2 py-0.5 font-mono text-sm text-brand-200">
                {WELCOME_PROMO_CODE}
              </code>
            </p>
            <div className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {SLOT_PLANS.map((plan) => (
                <div
                  key={`wel-${plan.id}`}
                  className={`relative flex flex-col rounded-2xl border-2 border-white/10 bg-white/[0.04] p-5 shadow-sm ${plan.accent}`}
                >
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                    {plan.period} · {plan.name}
                  </p>
                  <div className="mt-4 flex flex-wrap items-baseline gap-x-2 gap-y-1">
                    <span className="flex items-baseline gap-0.5">
                      <span className="text-3xl font-bold tabular-nums text-emerald-400">
                        {plan.price}
                      </span>
                      <span className="text-sm font-medium text-gray-400">
                        {plan.priceSuffix}
                      </span>
                    </span>
                    <span className="rounded-md border border-yellow-400/55 bg-yellow-400/20 px-2.5 py-1 text-sm font-extrabold tabular-nums text-yellow-200 shadow-[0_0_12px_rgba(250,204,21,0.25)] sm:text-base">
                      Save ${welcomeSavingsUsd(plan)}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-12 border-t border-white/10 pt-10">
              <h2 className="text-center text-sm font-bold uppercase tracking-wide text-sky-300">
                Welcome offer with your personal broker
              </h2>
              <p className="mx-auto mt-3 max-w-2xl text-center text-base font-semibold text-gray-200 sm:text-lg">
                Save 20% on all our subscriptions
              </p>
              <p className="mx-auto mt-2 max-w-2xl text-center text-sm text-gray-300">
                Use PromoCode:{" "}
                <code className="rounded-md bg-white/10 px-2 py-0.5 font-mono text-sm text-brand-200">
                  WLCMPB
                </code>
              </p>
              <p
                className="mx-auto mt-2 block w-fit rounded-xl border border-amber-400/55 bg-amber-400/15 px-3 py-1.5 text-center text-sm font-semibold text-amber-100"
                role="status"
              >
                This path is temporarily disabled.
              </p>
              <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                {SLOT_PLANS.map((plan) => (
                  <div
                    key={`pp-${plan.id}`}
                    className={`relative flex flex-col rounded-2xl border-2 border-white/10 bg-white/[0.04] p-5 shadow-sm ${plan.accent}`}
                  >
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                      {plan.period} · {plan.name}
                    </p>
                    <div className="mt-4 flex flex-wrap items-baseline gap-x-2 gap-y-1">
                      <span className="flex items-baseline gap-0.5">
                        <span className="text-3xl font-bold tabular-nums text-sky-300">
                          {plan.personalBrokerPriceLabel}
                        </span>
                        <span className="text-sm font-medium text-gray-400">
                          {plan.priceSuffix}
                        </span>
                      </span>
                      <span className="rounded-md border border-yellow-400/55 bg-yellow-400/20 px-2.5 py-1 text-sm font-extrabold tabular-nums text-yellow-200 shadow-[0_0_12px_rgba(250,204,21,0.25)] sm:text-base">
                        Save ${personalBrokerSavingsUsd(plan)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </div>

        <div className="mt-12 flex justify-center px-2">
          <Link
            to="/SignUp"
            className="inline-flex w-full max-w-xl items-center justify-center rounded-2xl bg-brand-500 px-10 py-5 text-center text-lg font-bold text-white shadow-lg shadow-brand-500/25 transition hover:bg-brand-600 sm:text-xl"
          >
            Join Us Now
          </Link>
        </div>

        <footer className="mt-8 border-t border-white/10 pt-5 text-center text-xs text-gray-400 sm:text-sm">
          <p>AlphaSniper 2026 © All Rights Reserved</p>
          <LegalFooterBannerAndLinks />
        </footer>
      </div>
    </main>
  );
}
